import React, { useEffect, useRef } from "react";
import {
  Bold,
  Italic,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  ImagePlus,
  Quote,
  Link as LinkIcon,
  Undo2,
  Redo2,
} from "lucide-react";
import { sanitizeHtml } from "../lib/sanitize";
import { compressImageToBase64 } from "../lib/imageCompression";

const ToolButton = ({ onClick, children, label, testId }) => (
  <button
    type="button"
    onClick={onClick}
    title={label}
    data-testid={testId}
    className="p-2 rounded-md hover:bg-muted text-foreground/70 hover:text-foreground transition-colors"
  >
    {children}
  </button>
);

/**
 * Lightweight rich-text editor (contenteditable based).
 * Props:
 *  - value (HTML string)
 *  - onChange(html)
 *  - placeholder
 *  - testIdPrefix (used to suffix tool buttons & editor)
 *  - minHeight (css value)
 */
export default function RichTextEditor({
  value = "",
  onChange,
  placeholder = "Start typing…",
  testIdPrefix = "rte",
  minHeight = "240px",
}) {
  const editorRef = useRef(null);
  const fileRef = useRef(null);
  const lastValue = useRef("");

  // Set initial / external value without disrupting cursor unnecessarily.
  useEffect(() => {
    if (editorRef.current && value !== lastValue.current) {
      const clean = sanitizeHtml(value);
      editorRef.current.innerHTML = clean;
      lastValue.current = clean;
    }
  }, [value]);

  const emit = () => {
    const html = sanitizeHtml(editorRef.current?.innerHTML || "");
    lastValue.current = html;
    onChange?.(html);
  };

  // Sanitize on paste — strip <script>, event handlers, dangerous URIs.
  const handlePaste = (e) => {
    const clipboard = e.clipboardData || window.clipboardData;
    const pasted = clipboard?.getData("text/html") || clipboard?.getData("text/plain") || "";
    if (!pasted) return;
    e.preventDefault();
    const clean = sanitizeHtml(pasted);
    document.execCommand("insertHTML", false, clean);
    emit();
  };

  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    emit();
  };

  const insertImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const b64 = await compressImageToBase64(file);
    editorRef.current?.focus();
    document.execCommand("insertHTML", false, `<p><img src="${b64}" alt="inline" /></p><p><br/></p>`);
    if (fileRef.current) fileRef.current.value = "";
    emit();
  };

  const insertLink = () => {
    const url = window.prompt("Enter URL");
    if (url) exec("createLink", url);
  };

  return (
    <div className="rounded-md border border-border overflow-hidden bg-white">
      <div className="flex items-center gap-1 flex-wrap p-2 border-b border-border bg-muted/30">
        <ToolButton onClick={() => exec("bold")} label="Bold" testId={`${testIdPrefix}-bold`}>
          <Bold className="size-4" />
        </ToolButton>
        <ToolButton onClick={() => exec("italic")} label="Italic" testId={`${testIdPrefix}-italic`}>
          <Italic className="size-4" />
        </ToolButton>
        <div className="w-px h-5 bg-border mx-1" />
        <ToolButton onClick={() => exec("formatBlock", "<h2>")} label="Heading 2" testId={`${testIdPrefix}-h2`}>
          <Heading2 className="size-4" />
        </ToolButton>
        <ToolButton onClick={() => exec("formatBlock", "<h3>")} label="Heading 3" testId={`${testIdPrefix}-h3`}>
          <Heading3 className="size-4" />
        </ToolButton>
        <ToolButton onClick={() => exec("formatBlock", "<blockquote>")} label="Quote" testId={`${testIdPrefix}-quote`}>
          <Quote className="size-4" />
        </ToolButton>
        <div className="w-px h-5 bg-border mx-1" />
        <ToolButton onClick={() => exec("insertUnorderedList")} label="Bulleted list" testId={`${testIdPrefix}-ul`}>
          <List className="size-4" />
        </ToolButton>
        <ToolButton onClick={() => exec("insertOrderedList")} label="Numbered list" testId={`${testIdPrefix}-ol`}>
          <ListOrdered className="size-4" />
        </ToolButton>
        <div className="w-px h-5 bg-border mx-1" />
        <ToolButton onClick={insertLink} label="Link" testId={`${testIdPrefix}-link`}>
          <LinkIcon className="size-4" />
        </ToolButton>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={insertImage}
          data-testid={`${testIdPrefix}-image-input`}
        />
        <ToolButton onClick={() => fileRef.current?.click()} label="Insert image" testId={`${testIdPrefix}-image`}>
          <ImagePlus className="size-4" />
        </ToolButton>
        <div className="w-px h-5 bg-border mx-1" />
        <ToolButton onClick={() => exec("undo")} label="Undo" testId={`${testIdPrefix}-undo`}>
          <Undo2 className="size-4" />
        </ToolButton>
        <ToolButton onClick={() => exec("redo")} label="Redo" testId={`${testIdPrefix}-redo`}>
          <Redo2 className="size-4" />
        </ToolButton>
      </div>

      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        onPaste={handlePaste}
        data-placeholder={placeholder}
        data-testid={`${testIdPrefix}-editor`}
        className="prose-content p-4 outline-none focus:outline-none"
        style={{ minHeight }}
      />
    </div>
  );
}
